import { AxiosError, AxiosInstance } from "axios";
import Rollbar from "rollbar";

import { rollbarConfig } from "../utils/environment";

const rollbarInstance = new Rollbar(rollbarConfig);

export const STATUSES_TO_IGNORE = [401, 403];

// Whitelist configuration for safe request body fields
const SAFE_REQUEST_FIELDS = {
  // Technical metadata (always safe)
  metadata: ["method", "url_path", "content_type", "request_size_bytes", "timestamp", "request_id"],

  // Business context (categorical data only, no PII or financial amounts)
  business: {
    use_of_funds: {
      purpose_kind: ["kind"] // Only log the category, not the amounts
    },
    source_of_funds: {
      product_kind: ["kind"] // Only log the product type, not amounts/names
    },
    // Other safe business fields
    inquiry_type: true,
    step: true,
    form_section: true
  }
};

const extractSafeRequestData = (payload: any) => {
  const request = payload?.config || payload?.request;
  if (!request) return null;

  const safeData: any = {};

  // Extract safe metadata
  safeData.metadata = {
    method: request.method?.toUpperCase(),
    url_path: extractPathFromUrl(request.url || request.responseURL),
    content_type: request.headers?.["Content-Type"]?.split(";")[0],
    request_size_bytes: request.data ? JSON.stringify(request.data).length : 0,
    timestamp: new Date().toISOString()
  };

  // Extract safe business data from request body
  if (request.data && typeof request.data === "object") {
    safeData.business_context = extractSafeBusinessData(request.data, SAFE_REQUEST_FIELDS.business);
  }

  return safeData;
};

const extractSafeBusinessData = (data: any, whitelist: any): any => {
  const result: any = {};

  for (const [key, config] of Object.entries(whitelist)) {
    if (!data.hasOwnProperty(key)) continue;

    if (config === true) {
      // Simple field whitelisting
      result[key] = data[key];
    } else if (Array.isArray(config)) {
      // Array configuration: config contains field names to extract from array elements
      if (Array.isArray(data[key])) {
        // Handle arrays like purpose_kind: [{kind: "real_estate", value: 50000}, ...]
        result[key] = extractSafeArrayData(data[key], config);
      } else if (typeof data[key] === "object" && data[key] !== null) {
        // Handle objects: extract specified fields
        result[key] = {};
        config.forEach(field => {
          if (data[key].hasOwnProperty(field)) {
            result[key][field] = data[key][field];
          }
        });
      }
    } else if (typeof config === "object" && config !== null) {
      // Nested object configuration
      if (typeof data[key] === "object" && data[key] !== null) {
        const nestedResult = extractSafeBusinessData(data[key], config);
        if (Object.keys(nestedResult).length > 0) {
          result[key] = nestedResult;
        }
      }
    }
  }

  return result;
};

// Special handling for arrays with whitelisted fields
const extractSafeArrayData = (arrayData: any[], allowedFields: string[]): any[] => {
  return arrayData
    .map(item => {
      if (typeof item !== "object" || item === null) return null;

      const safeItem: any = {};
      allowedFields.forEach(field => {
        if (item.hasOwnProperty(field)) {
          safeItem[field] = item[field];
        }
      });
      return Object.keys(safeItem).length > 0 ? safeItem : null;
    })
    .filter(item => item !== null);
};

const extractPathFromUrl = (url: string): string => {
  if (!url) return "unknown";
  try {
    // Remove query parameters and extract just the path
    return url.split("?")[0].replace(/^https?:\/\/[^/]+/, "");
  } catch {
    return url.split("?")[0] || "unknown";
  }
};

// Enhanced captureEvent with whitelist filtering
const captureEvent = (payload: any) => {
  if (!payload?.request && !payload?.response) return;

  const safeRequestData = extractSafeRequestData(payload);

  // Only capture if we have safe data to log
  if (safeRequestData) {
    rollbarInstance.captureEvent(
      {
        type: "HttpRequestFailed",
        url: payload.request?.responseURL || payload.config?.url,
        response: payload.response?.data,
        safe_request_data: safeRequestData
      },
      "debug"
    );
  }
};

export const logCritical = (errorMessage: string, payload?: any) => {
  // Capture only sanitized context to avoid recursive/circular structures
  captureEvent(payload);
  // Do not pass raw payloads to Rollbar to prevent RangeErrors on deep/circular objects
  rollbarInstance.critical(errorMessage);
};

export const logError = (errorMessage: string, payload?: any) => {
  // Capture only sanitized context to avoid recursive/circular structures
  captureEvent(payload);
  // Do not pass raw payloads to Rollbar to prevent RangeErrors on deep/circular objects
  rollbarInstance.error(errorMessage);
};

const getStatusFromError = (error: AxiosError) => {
  const { response } = error;
  const status = response ? response.status : null;

  return status;
};

const shouldLogToRollbar = (status: number | null) => {
  return !status || !STATUSES_TO_IGNORE.includes(status);
};

const getErrorMessage = (error: AxiosError) => {
  const status = getStatusFromError(error);

  let errorMessage = "Default error";

  if (status && error.response && error.response.config.method) {
    const {
      response: {
        config: { method, url }
      }
    } = error;

    errorMessage = `${status} for ${method.toUpperCase()} ${url}`;
  }

  return errorMessage;
};

export const statusCodeInterceptor = (error: AxiosError) => {
  const status = getStatusFromError(error);

  if (shouldLogToRollbar(status)) {
    const errorMessage = getErrorMessage(error);

    logError(errorMessage, error);
  }
};

export const addStatusCodeInterceptorToInstance = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    res => res,
    error => {
      statusCodeInterceptor(error);
      throw error;
    }
  );
};
